import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import classnames from 'classnames';
import { Field, Form, Formik } from 'formik';
import { store } from 'react-notifications-component';
import * as yup from 'yup';
import { REQUIRED_VALIDATION, INVALID_EMAIL } from 'consts/form';
import FormError from 'components/common/FormError';
import LoadingAnimation from 'components/common/LoadingAnimation';
import Select from 'react-select';

import { signinRequest } from 'redux/actions/authActions';
import { validate, updateUser, getSystemLookup } from 'api/authApi';
import './index.scss';
import FormTime from 'modules/ui/form/formTime';
import AddressPickerField from 'components/common/AddressPickerField';

const validationSchema = yup.object().shape({
  firstName: yup.string().required(REQUIRED_VALIDATION),
  lastName: yup.string().required(REQUIRED_VALIDATION),
  email: yup
    .string()
    .required(REQUIRED_VALIDATION)
    .email(INVALID_EMAIL),

  password: yup.string(),
  passwordConfirmation: yup.string().when('password', {
    is: val => !!(val && val.length > 0),
    then: yup
      .string()
      .oneOf([yup.ref('password')], 'Both password need to be the same'),
  }),

  companyName: yup.string().required(REQUIRED_VALIDATION),
  city: yup.string().required(REQUIRED_VALIDATION),
  address1: yup.string().required(REQUIRED_VALIDATION),
  address2: yup.string(),
  phone: yup.string().required(REQUIRED_VALIDATION),
  zip: yup.string().required(REQUIRED_VALIDATION),
});

const AccountSettings = ({ user, signinRequest }) => {
  const userSignedIn = user && user.user && user.user.confirmed;
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const defaultCutOffTime = '5:00 PM';
  const defaultDeliveryTime = '11:30 AM';
  const [initialValues, setInitialValues] = useState({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    zip: '',
    city: '',
    companyName: '',
    phone: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    aboutMe: '',
    defaultDeliveryInstructions: '',
  });
  const [dietaryTypes, setDietaryTypes] = useState([]);
  const [cuisineType, setCuisineTypes] = useState([]);
  const minDate = null;
  const fetchData = async () => {
    try {
      const userInfo = await validate();
      setInitialValues({
        firstName: (userInfo && userInfo.firstName) || '',
        lastName: (userInfo && userInfo.lastName) || '',
        address1: (userInfo && userInfo.address1) || '',
        address2: (userInfo && userInfo.address2) || '',
        zip: (userInfo && userInfo.zip) || '',
        city: (userInfo && userInfo.city) || '',
        companyName: (userInfo && userInfo.companyName) || '',
        phone: (userInfo && userInfo.phone) || '',
        email: (userInfo && userInfo.email) || '',
        password: (userInfo && userInfo.password) || '',
        passwordConfirmation: (userInfo && userInfo.passwordConfirmation) || '',
        aboutMe: (userInfo && userInfo.aboutMe) || '',
        defaultDeliveryInstructions:
          (userInfo && userInfo.defaultDeliveryInstructions) || '',
        favoriteCousineTypes: (userInfo && userInfo.favoriteCousineTypes) || [],
        dietaryTypes: (userInfo && userInfo.dietaryTypes) || [],
        goGapDays: (userInfo && userInfo.goGapDays) || 0,
        goCutoffTime:
          userInfo && userInfo?.goCutoffTime && userInfo?.goCutoffTime > 0
            ? format(
                new Date(userInfo?.goCutoffTime * 1000),
                'h:mm aaa',
              ).toUpperCase()
            : defaultCutOffTime,

        date: userInfo?.goDeliveryTime
          ? new Date(userInfo?.goDeliveryTime * 1000)
          : new Date(),
        cuttoffDate: userInfo?.goCutoffTime
          ? new Date(userInfo?.goCutoffTime * 1000)
          : new Date(),
        goDeliveryTime:
          userInfo && userInfo?.goDeliveryTime && userInfo?.goDeliveryTime > 0
            ? format(
                new Date(userInfo?.goDeliveryTime * 1000),
                'h:mm aaa',
              ).toUpperCase()
            : defaultDeliveryTime,
        goHeadCount: userInfo?.goHeadCount ? userInfo?.goHeadCount : 0,
        goBudgetCents: userInfo?.goBudgetCents
          ? userInfo?.goBudgetCents / 100
          : 0,
        goName: userInfo?.goName || '',
      });

      const { presetDietaryType, presetCuisineType } = await getSystemLookup([
        'preset_dietary_type',
        'preset_cuisine_type',
      ]);

      setDietaryTypes(presetDietaryType);
      setCuisineTypes(presetCuisineType);
    } catch (e) {
      console.log('---> error', e);
      store.addNotification({
        message:
          'Sorry, we are not able to process your request now. Please try again later.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: {
          duration: 4000,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const updateData = async ({
    goDeliveryTime,
    goCutoffTime,
    date,
    cuttoffDate,
    goBudgetCents,
    ...form
  }) => {
    try {
      const deliveryTime = date.setHours(
        goDeliveryTime.split(/[\s:]+/)[2] === 'PM' &&
          +goDeliveryTime.split(/[\s:]+/)[0] !== 12
          ? +goDeliveryTime.split(/[\s:]+/)[0] + 12
          : +goDeliveryTime.split(/[\s:]+/)[0],
        goDeliveryTime.split(/[\s:]+/)[1],
        0,
      );
      const cuttoffTime = cuttoffDate.setHours(
        goCutoffTime.split(/[\s:]+/)[2] === 'PM' &&
          +goCutoffTime.split(/[\s:]+/)[0] !== 12
          ? +goCutoffTime.split(/[\s:]+/)[0] + 12
          : +goCutoffTime.split(/[\s:]+/)[0],
        goCutoffTime.split(/[\s:]+/)[1],
        0,
      );
      const updated = await updateUser({
        user: {
          ...form,
          goDeliveryTime: Math.abs(deliveryTime),
          goCutoffTime: Math.abs(cuttoffTime),
          goBudgetCents: goBudgetCents * 100,
        },
      });
      setInitialValues({
        ...updated,
        date: updated?.goDeliveryTime
          ? new Date(updated?.goDeliveryTime * 1000)
          : new Date(),
        cuttoffDate: updated?.goCutoffTime
          ? new Date(updated?.goCutoffTime * 1000)
          : new Date(),
        goBudgetCents: updated?.goBudgetCents / 100,
        goDeliveryTime: format(
          updated?.goDeliveryTime && updated?.goDeliveryTime > 0
            ? new Date(updated?.goDeliveryTime * 1000)
            : new Date(),
          'h:mm aaa',
        ).toUpperCase(),
        goCutoffTime: format(
          updated?.goCutoffTime && updated?.goCutoffTime > 0
            ? new Date(updated?.goCutoffTime * 1000)
            : new Date(),
          'h:mm aaa',
        ).toUpperCase(),
      });
      signinRequest({ user: updated, jwt: user.jwt });
      store.addNotification({
        message: 'Successfully updated!',
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: {
          duration: 4000,
        },
      });
    } catch (e) {
      console.log('---> error', e);
      store.addNotification({
        message:
          'Sorry, we are not able to process your request now. Please try again later.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: {
          duration: 4000,
        },
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!userSignedIn) {
      history.push('/');
    }
  });
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const onSubmit = form => {
    setLoading(true);
    updateData(form);
  };

  return (
    <main className="settings-page">
      {loading && <LoadingAnimation />}
      {!loading && (
        <div className="page-content page-card-container">
          <div className="setting-page-body page-card">
            <h3 className="setting-page-title mt-0 mb-4">Account Settings</h3>
            <Formik
              {...{ initialValues, validationSchema }}
              validateOnChange
              validateOnBlur
              onSubmit={onSubmit}
            >
              {({
                setFieldValue,
                values,
                touched,
                handleBlur,
                setFieldTouched,
              }) => (
                <Form className="setting-form">
                  <div className="row">
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.firstName && values.firstName,
                          })}
                        >
                          First name
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="firstName"
                          placeholder=""
                        />
                        <FormError name="firstName" />
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.lastName && values.lastName,
                          })}
                        >
                          Last name
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="lastName"
                        />
                        <FormError name="lastName" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-sm-8">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.address1 && values.address1,
                          })}
                        >
                          Delivery Address
                        </label>
                        <AddressPickerField
                          id="setting-address"
                          name="address1"
                          value={values.address1}
                          defaultAddress={values?.address1}
                          setCityPostalCode={({ postalCode, city }) => {
                            setFieldValue('zip', postalCode);
                            setFieldValue('city', city);
                          }}
                          addAddressLocal={false}
                          onChange={setFieldValue}
                          onFocus={() => {
                            setFieldTouched('address1');
                          }}
                          onBlur={e => {
                            handleBlur(e);
                          }}
                        />
                        <FormError name="address1" />
                      </div>
                    </div>
                    <div className="col-12 col-sm-4">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.address2 && values.address2,
                          })}
                        >
                          &nbsp;
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="address2"
                          placeholder="Suite #"
                        />
                        <FormError name="address2" />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 col-sm-4">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused': touched.zip && values.zip,
                          })}
                        >
                          Postal code
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="zip"
                          placeholder="M9B4R7"
                        />
                        <FormError name="zip" />
                      </div>
                    </div>
                    <div className="col-12 col-sm-4">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused': touched.city && values.city,
                          })}
                        >
                          City
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="city"
                          placeholder="ETOBICOKE"
                        />
                        <FormError name="city" />
                      </div>
                    </div>

                    <div className="col-12 col-sm-4">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.companyName && values.companyName,
                          })}
                        >
                          Company Name
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="companyName"
                        />
                        <FormError name="companyName" />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused': touched.phone && values.phone,
                          })}
                        >
                          Phone Number
                          <span
                            className="small text-primary"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Help info"
                          >
                            (?)
                          </span>
                        </label>
                        <Field
                          className="form-control"
                          type="text"
                          name="phone"
                        />
                        <FormError name="phone" />
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused': touched.email && values.email,
                          })}
                        >
                          Email address
                        </label>
                        <Field
                          disabled
                          className="form-control"
                          type="email"
                          name="email"
                        />
                        <FormError name="email" />
                        <div className="labeled-group__danger">
                          This is your sign-in email and not editable
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.password && values.password,
                          })}
                        >
                          Password
                        </label>
                        <Field
                          className="form-control"
                          type="password"
                          name="password"
                        />
                        <FormError name="password" />
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.passwordConfirmation &&
                              values.passwordConfirmation,
                          })}
                        >
                          Confirm Password
                        </label>
                        <Field
                          className="form-control"
                          type="password"
                          name="passwordConfirmation"
                        />
                        <FormError name="passwordConfirmation" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused': touched.aboutMe && values.aboutMe,
                          })}
                        >
                          About me
                        </label>
                        <Field
                          className="form-control"
                          name="aboutMe"
                          as="textarea"
                          rows={10}
                          placeholder="Tell us a bit about yourself and company so we can better serve you."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.defaultDeliveryInstructions &&
                              values.defaultDeliveryInstructions,
                          })}
                        >
                          Default Delivery Instructions
                        </label>
                        <Field
                          className="form-control"
                          as="textarea"
                          name="defaultDeliveryInstructions"
                          placeholder="E.g. Please bring the package to reception on the 3rd floor and call Paula"
                          rows={4}
                        />
                        <FormError name="defaultDeliveryInstructions" />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.dietaryTypes && values.dietaryTypes,
                          })}
                        >
                          Dietary Types
                        </label>
                        <Select
                          name="dietaryTypes"
                          isMulti
                          options={dietaryTypes.map(type => {
                            return { label: type, value: type };
                          })}
                          defaultValue={
                            initialValues &&
                            initialValues.dietaryTypes &&
                            initialValues.dietaryTypes.map(type => {
                              return { label: type, value: type };
                            })
                          }
                          onChange={option => {
                            console.log(option);
                            setFieldValue(
                              'dietaryTypes',
                              option.map(item => {
                                return item.value;
                              }),
                            );
                          }}
                          onBlur={handleBlur}
                          onFocus={() => {
                            setFieldTouched('dietaryTypes');
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <div className="form-group">
                        <label
                          className={classnames({
                            'label-focused':
                              touched.cuisineTypes && values.cuisineTypes,
                          })}
                        >
                          Cuisine Types
                        </label>
                        <Select
                          name="favoriteCousineTypes"
                          isMulti
                          options={cuisineType.map(type => {
                            return { label: type, value: type };
                          })}
                          defaultValue={
                            initialValues &&
                            initialValues.favoriteCousineTypes &&
                            initialValues.favoriteCousineTypes.map(type => {
                              return { label: type, value: type };
                            })
                          }
                          onChange={option => {
                            setFieldValue(
                              'favoriteCousineTypes',
                              option.map(item => {
                                return item.value;
                              }),
                            );
                          }}
                          onBlur={handleBlur}
                          onFocus={() => {
                            setFieldTouched('favoriteCousineTypes');
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="setting-page-title mb-4 ">
                      Corporate Order Settings
                    </h3>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-group">
                          <label
                            className={classnames({
                              'label-focused': touched.goName && values.goName,
                            })}
                          >
                            Name
                          </label>
                          <Field
                            className="form-control"
                            name="goName"
                            as="textarea"
                            rows={10}
                            placeholder="Enter the grouporder name "
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-sm-6">
                        <div className="form-group">
                          <label
                            className={classnames({
                              'label-focused':
                                touched.goHeadCount && values.goHeadCount,
                            })}
                          >
                            Head Count
                          </label>
                          <Field
                            className="form-control"
                            type="text"
                            name="goHeadCount"
                            placeholder="50"
                          />
                          <FormError name="goHeadCount" />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6">
                        <div className="form-group">
                          <label
                            className={classnames({
                              'label-focused':
                                touched.goBudgetCents && values.goBudgetCents,
                            })}
                          >
                            Per Person Budget
                          </label>
                          <Field
                            className="form-control"
                            type="text"
                            name="goBudgetCents"
                            placeholder="25"
                          />
                          <FormError name="goBudgetCents" />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 ">
                        <div className="form-group">
                          <label
                            className={classnames({
                              'label-focused':
                                touched.goGapDays && values.goGapDays,
                            })}
                          >
                            Gap Days (minimum 1)
                          </label>
                          <Field
                            className="form-control"
                            name="goGapDays"
                            type="number"
                            min="1"
                            rows={10}
                            placeholder="2"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-sm-6">
                        <div className="form-group">
                          <label
                            className={classnames({
                              'label-focused':
                                touched.goCutoffTime && values.goCutoffTime,
                            })}
                          >
                            Cut Off Time
                          </label>
                          <FormTime
                            className="form-control"
                            value={values?.date}
                            minDate={minDate}
                            name={'goCutoffTime'}
                          />
                          <FormError name="goCutoffTime" />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6">
                        <div className="form-group">
                          <label
                            className={classnames({
                              'label-focused':
                                touched.deliveryTime && values.deliveryTime,
                            })}
                          >
                            Delivery Time
                          </label>
                          <FormTime
                            className="form-control"
                            value={values?.date}
                            minDate={minDate}
                            name={'goDeliveryTime'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pq-row">
                    <div className="d-flex justify-content-end mt-3">
                      <button
                        type="submit"
                        className="btn btn-primary settings-btn"
                      >
                        SAVE
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </main>
  );
};
const mapStateToProps = state => ({
  user: state && state.user,
});

const mapDispatchToProps = {
  signinRequest,
};

AccountSettings.propTypes = {
  user: PropTypes.shape({
    user: PropTypes.shape({
      firstName: PropTypes.string,
      confirmed: PropTypes.bool,
    }),
    jwt: PropTypes.string,
  }).isRequired,
  signinRequest: PropTypes.func.isRequired,
};
const connectedHeader = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountSettings);

export default connectedHeader;
